import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import T, { t } from '../../../../common/components/T';
import Skeleton from '../../../../widgets/plug/Skeleton/Skeleton';
import css from './BranchDynamics.module.scss';
import { blockSkeleton, colors, createWidgetData } from './helpers';
import UserContext, { UserProfileContext } from '../../../../common/utils/getContext';
import { getData } from '../../../../common/utils/router';
import { LangContext } from '../../../../common/decorators/Language';
import Plug from '../../../../widgets/plug/Plug/Plug';
import no_period from '../../../../public/src/img/plugs/no_chart_area_data.svg';
import HighChart from '../../../../hybrid/library/HighCharts';
import CalendarFilterButton from '../CalendarFilterButton/CalendarFilterButton';
import { getMemoizedData } from '../../../../common/utils/memo';

const RenderData = ({ response: allData }) => {
    const { currencies, userToken, lang } = useContext(UserContext);
    const { profileData } = useContext(UserProfileContext);
    const [periods, setPeriods] = useState([]);

    useEffect(() => {
        getMemoizedData('/api/v1/periods', userToken, lang).then((res) => {
            if (Array.isArray(res?.data)) setPeriods(res?.data?.sort((a, b) => +a.id - +b.id));
        });
    }, [userToken, lang]);

    // фільтр при скиданні даних в фільтрі календаря
    const resetFilterParams = useMemo(() => {
        if (!profileData) return '';
        const { marketing_profile: m } = profileData;
        return `filter[marketing_period_id_from]=${m?.marketing_period_id || 1}&filter[marketing_period_id_to]=${
            m?.marketing_period_id_current
        }`;
    }, [profileData]);

    const [chart, setChart] = useState(null);
    const { translation } = useContext(LangContext);
    const currency = useMemo(() => currencies?.data?.find((curr) => curr.main), [currencies]);

    useEffect(() => {
        setChart(createWidgetData(allData, currency, translation, periods));
    }, [allData, currency, translation, periods]);

    return (
        <div className={css.block}>
            <div className={css.head}>
                <div className={css.headLeft}>
                    <p className={css.title}>
                        <T
                            textName='branch-dynamics-in-marketing'
                            defaultStr='Динамика развития веток в маркетинге'
                            page='/office'
                        />
                    </p>
                    <p className={css.period}>
                        <span className={css.periodLabel}>
                            <T textName='marketing_period' defaultStr='Маркетинговый период' page='/office' />:
                        </span>
                        <span className={css.periodValue}>
                            {chart?.period?.start_formatted === chart?.period?.end_formatted
                                ? ` ${chart?.period?.end_formatted}`
                                : ` ${chart?.period?.start_formatted} - ${chart?.period?.end_formatted}`}
                        </span>
                    </p>
                </div>
                <div className={css.headRight}>
                    <CalendarFilterButton
                        url='/api/v1/user/reports/structure-branches'
                        resetFilterParams={resetFilterParams}
                        setData={(data) => setChart(createWidgetData(data, currency, translation, periods))}
                        attributes={['marketing_period_id_from', 'marketing_period_id_to']}
                        initValue={{
                            marketing_period_id_from: { value: chart?.period?.start, label: chart?.period?.start },
                            marketing_period_id_to: { value: chart?.period?.end, label: chart?.period?.end }
                        }}
                    />
                </div>
            </div>
            <div className={css.stats}>
                <div className={css.statsItem}>
                    <div className={css.statsItemHead}>
                        <div className={css.statsItemCircle} style={{ backgroundColor: colors[0] }}></div>
                        <p className={css.statsItemLabel}>
                            <T textName='dev-of-the-left-branch' defaultStr='Развитие левой ветки' page='/office' />
                        </p>
                    </div>
                    <div className={css.statsItemBody}>
                        <span className={css.statsItemValue}>{(+chart?.branches?.left).toLocaleString()}</span>
                        <span className={css.statsItemCurr}>{chart?.currency?.symbol || 'PV'}</span>
                    </div>
                </div>
                <div className={css.statsItem}>
                    <div className={css.statsItemHead}>
                        <div className={css.statsItemCircle} style={{ backgroundColor: colors[1] }}></div>
                        <p className={css.statsItemLabel}>
                            <T textName='dev-of-the-right-branch' defaultStr='Развитие правой ветки' page='/office' />
                        </p>
                    </div>
                    <div className={css.statsItemBody}>
                        <span className={css.statsItemValue}>{(+chart?.branches?.right).toLocaleString()}</span>
                        <span className={css.statsItemCurr}>{chart?.currency?.symbol || 'PV'}</span>
                    </div>
                </div>
            </div>
            {
                // якщо немає суми з лівої або правої гілки тоді виводити заглушку
                +chart?.branches?.left || +chart?.branches?.right ? (
                    <div className={css.chart}>
                        <HighChart componentConfig={chart?.config} />
                    </div>
                ) : (
                    <Plug
                        plugTitle={t(
                            'receivedBonusesNoPeriod_stat-plugTitle',
                            'Данных за указанный период нет',
                            '/office',
                            translation
                        )}
                        plugDescription={t(
                            'receivedBonusesNoPeriod_stat-plugDescription',
                            'Измените условия фильтрации',
                            '/office',
                            translation
                        )}
                        plugImg={no_period}
                        classes={{ plugContainer: [css.plugContainer] }}
                    />
                )
            }
        </div>
    );
};

export default function BranchDynamics() {
    const { userToken, lang } = useContext(UserContext);
    const { profileData } = useContext(UserProfileContext);

    const loader = useCallback(() => {
        if (!profileData) return;
        const { marketing_profile: markData } = profileData;
        return getData(
            `/api/v1/user/reports/structure-branches?filter[marketing_period_id_from]=${
                markData?.marketing_period_id || 1
            }&filter[marketing_period_id_to]=${markData?.marketing_period_id_current}`,
            userToken,
            lang
        );
    }, [userToken, lang, profileData]);

    return <Skeleton handler={loader} Component={RenderData} pattern={blockSkeleton} wrapperStyles={css.block} lazy={true} />;
}
